<template>
  <div data-test-id="adminsView">
    <DataTable
      :editable="appStore.isProgramAdmin"
      :row-props="colorActiveRow"
      :protected-item-ids="protectedItemIds"
      :disable-delete-message="
        store.selectedItem?.sub === appStore.auth0User.sub
          ? t('warnings.deleteActiveAdmin')
          : t('warnings.deleteAdminWithAppointments')
      "
      :table-store-id="TableStoreId.Admins"
      :delete-message="deleteMessage"
      :delete-title="t('dialogs.headers.deleteAdmin')"
      @new="createNewItem"
    >
      <template v-slot:menuItems="{ item }">
        <v-list-item
          density="compact"
          :title="t('buttons.viewAppointments')"
          prepend-icon="mdi-calendar-clock"
          @click="showAppointments(item)"
        />
      </template>
      <template v-slot:item.email="{ item }">
        <v-icon
          v-if="item.email"
          v-tooltip="{ openOnClick: true, text: item.email }"
          size="small"
          @click="copyToClipboard(item)"
        >
          mdi-email
        </v-icon>
        <v-icon
          v-else
          v-tooltip="{ openOnClick: true, text: t('warnings.missingEmail') }"
          @click="store.editItem(item)"
          :color="Colors.accentWarning"
          size="small"
        >
          mdi-alert
        </v-icon>
      </template>

      <template v-slot:item.role="{ item }">
        {{ store.getRoleName(item.role) }}
      </template>

      <template v-slot:item.lastUpdated="{ item }">
        {{ isDefined(item.lastUpdated) ? formatDate(item.lastUpdated, true) : t('labels.dash') }}
      </template>
      <template v-slot:footer>
        <EditAdmin />
        <EditAppointments v-model="store.showEditAppointments" />
      </template>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { formatDate, getEarliestDateTime, isDefined } from '@/composables/utils'
import { MessageType } from '@/enums/MessageType'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IAdminsStore } from '@/interfaces/api/IAdminsStore'
import type { IEventsStore } from '@/interfaces/api/IEventsStore'
import { AdminRecord } from '@/models/AdminRecord'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useAppStore } from '@/stores/ui/AppStore'
import { useSnackbarStore } from '@/stores/ui/SnackbarStore'
import Colors from '@/styles/_colors.module.scss'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })
const appStore = useAppStore()
const eventsStore = TableStoreFactory.get(TableStoreId.Events) as IEventsStore
const snackbarStore = useSnackbarStore()
const store = TableStoreFactory.get(TableStoreId.Admins) as IAdminsStore

// PROPS & EMITS

// REACTIVE VARIABLES

// COMPUTED PROPERTIES
const deleteMessage = computed((): string => {
  return t('dialogs.delete.adminWarning', {
    name: `${store.selectedItem?.firstName} ${store.selectedItem?.lastName}`
  })
})

const protectedItemIds = computed(() => {
  return store.items
    .filter((admin: AdminRecord) => {
      return admin.sub === appStore.auth0User.sub || admin.appointments > 0
    })
    .map((item) => item.id) as string[]
})

// LIFECYCLE HOOKS

// FUNCTIONS
const copyToClipboard = (item: AdminRecord) => {
  if (item?.email) {
    navigator.clipboard.writeText(item.email)
    snackbarStore.addMessage(t('text.copiedToClipboard'), MessageType.Info)
  }
}

const colorActiveRow = (row: any) => {
  if (row.item.sub === appStore.auth0User.sub) {
    return { style: `background-color: ${Colors.mainAccent};` }
  }
}

const createNewItem = () => {
  store.createNewItem(new AdminRecord())
}

const loadEvents = async (id: number) => {
  const date = getEarliestDateTime()
  const today = date.toISOString()
  eventsStore.setEventsFilterForOrganizer(id, today)
  eventsStore.loadItems()
}

const showAppointments = async (item: AdminRecord) => {
  if (item?.id) {
    await loadEvents(item.id)
    store.editItem(item, false)
    store.showEditAppointments = true
  }
}
</script>

<style lang="scss" scoped>
.active-row {
  background-color: red;
  font-weight: bold;
}
</style>
